"use client";

import { createContext, useContext, useMemo, useState } from "react";

/** Cell weight fill orientation. Default = vertical (current legacy behavior). */
export type CellFill = "vertical" | "horizontal";

interface ChromeContextValue {
  sidebarOpen: boolean;
  setSidebarOpen: (v: boolean) => void;
  importOpen: boolean;
  setImportOpen: (v: boolean) => void;
  exportOpen: boolean;
  setExportOpen: (v: boolean) => void;
  mergeOpen: boolean;
  setMergeOpen: (v: boolean) => void;
  cellFill: CellFill;
  setCellFill: (next: CellFill) => void;
}

const Ctx = createContext<ChromeContextValue | null>(null);

export function ChromeProvider({ children }: { children: React.ReactNode }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [cellFill, setCellFill] = useState<CellFill>("vertical");

  const value = useMemo<ChromeContextValue>(
    () => ({
      sidebarOpen,
      setSidebarOpen,
      importOpen,
      setImportOpen,
      exportOpen,
      setExportOpen,
      mergeOpen,
      setMergeOpen,
      cellFill,
      setCellFill,
    }),
    [sidebarOpen, importOpen, exportOpen, mergeOpen, cellFill],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useChrome() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useChrome must be used inside <ChromeProvider>");
  return v;
}
